import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Calendar, User, Clock, ArrowRight } from "lucide-react";
import { SiLinkedin } from "react-icons/si";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import type { CaseStudy } from "@shared/blog-schema";
import { WebsiteHeader } from "@/components/layout/website-header";
import { WebsiteFooter } from "@/components/layout/website-footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const caseStudiesFaqs = [
  { q: "How do businesses measure the success of SaaS management?", a: "SaaSGama case studies showcase how organizations improve visibility, efficiency, and software decision-making." },
  { q: "What results can businesses achieve with better SaaS management?", a: "SaaSGama case studies highlight real examples of improved software oversight and operational efficiency." },
  { q: "How do companies gain control of their software ecosystem?", a: "SaaSGama helps businesses centralize software information and create a more structured management approach." },
  { q: "Can businesses improve software visibility across teams?", a: "SaaSGama case studies demonstrate how organizations gain greater visibility into software usage and investments." },
  { q: "How do organizations make smarter software decisions?", a: "SaaSGama provides actionable insights that help businesses evaluate and optimize their software environment." },
  { q: "What challenges can businesses overcome with SaaS management?", a: "SaaSGama helps organizations address software complexity, fragmented data, and limited visibility." },
  { q: "How can companies improve operational efficiency through software management?", a: "SaaSGama helps streamline software oversight, making it easier to manage applications across the business." },
  { q: "Why do businesses look for SaaS management success stories?", a: "SaaSGama case studies provide real-world examples of how organizations improve software management outcomes." },
  { q: "How can organizations build a more efficient software ecosystem?", a: "SaaSGama helps businesses create visibility, improve accountability, and support better technology decisions." },
  { q: "What can businesses learn from SaaS management case studies?", a: "SaaSGama case studies offer practical insights into software optimization, visibility, and operational improvement." },
];

export default function CaseStudies() {
  const [,] = useLocation();

  const { data: caseStudies = [], isLoading } = useQuery<CaseStudy[]>({
    queryKey: ['/api/case-studies'],
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(' ').length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return readTime;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading case studies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>SaaSGama Case Studies | Real SaaS Cost Saving Results</title>
        <meta name="description" content="Explore SaaSGama case studies and see how businesses reduce SaaS costs, improve license visibility, eliminate waste and maximize ROI." />
        <meta name="keywords" content="SaaS Case Studies, SaaS Cost Optimization, License Management, Software Spend Management, SaaS Cost Savings" />
        <link rel="canonical" href="https://saasgama.com/case-studies" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://saasgama.com/case-studies" />
        <meta property="og:site_name" content="SaaSGama" />
        <meta property="og:title" content="SaaSGama Case Studies | Real SaaS Cost Saving Results" />
        <meta property="og:description" content="Explore SaaSGama case studies and see how businesses reduce SaaS costs, improve license visibility, eliminate waste and maximize ROI." />
        <meta property="og:image" content="https://saasgama.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SaaSGama Case Studies | Real SaaS Cost Saving Results" />
        <meta name="twitter:description" content="Explore SaaSGama case studies and see how businesses reduce SaaS costs, improve license visibility, eliminate waste and maximize ROI." />
        <meta name="twitter:image" content="https://saasgama.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Organization", "@id": "https://saasgama.com/#organization", "name": "SaaSGama", "url": "https://saasgama.com", "logo": "https://saasgama.com/logo.png", "description": "SaaSGama helps businesses improve software visibility, optimize software investments, and simplify SaaS management." },
            { "@type": "BreadcrumbList", "@id": "https://saasgama.com/case-studies#breadcrumb", "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://saasgama.com" },
              { "@type": "ListItem", "position": 2, "name": "Case Studies", "item": "https://saasgama.com/case-studies" }
            ]},
            { "@type": "CollectionPage", "@id": "https://saasgama.com/case-studies#collectionpage", "url": "https://saasgama.com/case-studies", "name": "SaaSGama Case Studies", "description": "Explore SaaSGama case studies and learn how organizations improve software visibility, operational efficiency, and software management outcomes." },
            { "@type": "WebPage", "@id": "https://saasgama.com/case-studies#webpage", "url": "https://saasgama.com/case-studies", "name": "SaaSGama Case Studies", "description": "Discover real-world SaaSGama success stories, software optimization examples, and SaaS management outcomes.", "breadcrumb": { "@id": "https://saasgama.com/case-studies#breadcrumb" }, "isPartOf": { "@id": "https://saasgama.com/#organization" } },
            { "@type": "FAQPage", "@id": "https://saasgama.com/case-studies#faq", "mainEntity": caseStudiesFaqs.map(faq => ({ "@type": "Question", "name": faq.q, "acceptedAnswer": { "@type": "Answer", "text": faq.a } })) }
          ]
        })}</script>
      </Helmet>

      <WebsiteHeader showBackButton />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20 pt-32">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            SaaSgama <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Case Studies</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Discover how organizations are transforming their software management and achieving significant cost savings with SaaSGama
          </p>
          <div className="flex items-center justify-center gap-4 text-blue-100">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              <span>Real Results</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
              <span>Proven ROI</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-400 rounded-full"></div>
              <span>Industry Leaders</span>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
            {/* Featured Case Study */}
            {caseStudies.length > 0 && (
              <section className="mb-12">
                <h2 className="text-2xl font-bold mb-6 text-center">Featured Case Study</h2>
                <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300 max-w-4xl mx-auto">
                  <div className="md:flex md:items-center">
                    {caseStudies[0].featuredImage && (
                      <div className="md:w-2/5">
                        <img 
                          src={caseStudies[0].featuredImage} 
                          alt={caseStudies[0].title}
                          className="w-full h-48 md:h-64 object-cover"
                        />
                      </div>
                    )}
                    <div className={`${caseStudies[0].featuredImage ? 'md:w-3/5' : 'w-full'} p-6`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="default" className="bg-green-500 hover:bg-green-600 text-xs">
                          Featured
                        </Badge>
                        {caseStudies[0].tags && caseStudies[0].tags.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {caseStudies[0].tags[0]}
                          </Badge>
                        )}
                      </div>
                      
                      <h3 className="text-xl md:text-2xl font-bold mb-3 line-clamp-2">
                        {caseStudies[0].title}
                      </h3>
                      
                      <p className="text-gray-600 mb-4 text-base leading-relaxed line-clamp-3">
                        {caseStudies[0].excerpt}
                      </p>
                      
                      <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {caseStudies[0].authorName}
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate((caseStudies[0].publishedAt || caseStudies[0].createdAt).toString())}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatReadTime(caseStudies[0].content)} min read
                        </div>
                      </div>
                      
                      <Button asChild>
                        <Link href={`/case-studies/${caseStudies[0].slug}`}>
                          Read Case Study
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </Card>
              </section>
            )}

            {/* All Case Studies Grid */}
            {caseStudies.length > 1 && (
              <section>
                <h2 className="text-3xl font-bold mb-8 text-center">All Case Studies</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {caseStudies.slice(1).map((caseStudy) => (
                    <Card key={caseStudy.id} className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
                      {caseStudy.featuredImage && (
                        <div className="relative overflow-hidden">
                          <img 
                            src={caseStudy.featuredImage} 
                            alt={caseStudy.title}
                            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-300"></div>
                        </div>
                      )}
                      
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-2 mb-3">
                          {caseStudy.tags && caseStudy.tags.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {caseStudy.tags[0]}
                            </Badge>
                          )}
                        </div>
                        
                        <h3 className="text-xl font-semibold mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                          {caseStudy.title}
                        </h3>
                        
                        <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                          {caseStudy.excerpt}
                        </p>
                      </CardHeader>
                      
                      <CardContent className="pt-0">
                        <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate((caseStudy.publishedAt || caseStudy.createdAt).toString())}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatReadTime(caseStudy.content)} min
                          </div>
                        </div>
                        
                        <Button asChild variant="outline" className="w-full group-hover:bg-blue-50 group-hover:border-blue-200">
                          <Link href={`/case-studies/${caseStudy.slug}`}>
                            Read More
                            <ArrowRight className="h-3 w-3 ml-2" />
                          </Link>
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            )}
      </div>

      {/* FAQ Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-800">Frequently Asked Questions</h2>
              <p className="text-base md:text-xl text-gray-600">
                Common questions about SaaSGama case studies and success stories
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {caseStudiesFaqs.map((faq, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border border-gray-200 rounded-lg px-6 bg-white shadow-sm hover:shadow-md transition-shadow">
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-800 hover:text-blue-600">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pt-2">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Call to Action - Full Width */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Create Your Own Success Story?
            </h2>
            <p className="text-xl mb-8 text-blue-100">
              Join hundreds of organizations that have transformed their software management with SaaSGama
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-xl shadow-lg"
                asChild
              >
                <Link href="/register" className="flex items-center">
                  Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 text-lg font-semibold rounded-xl transition-all bg-white"
                asChild
              >
                <Link href="/contact">Talk to Sales</Link>
              </Button>
            </div>

            <div className="mt-8 text-blue-200 text-sm">
              Start your journey to significant cost savings and improved efficiency today
            </div>
          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
}